import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ClipboardCheck, CheckCircle2, AlertTriangle, Loader2, Lock } from "lucide-react";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function TechnicalReview({ lead, onUpdate, canEdit, projectOwnerSet }) {
  const an = lead.analysis || {};
  const pvgisYield = an.pvgis?.specific_yield ?? null;

  const initial = {
    f1_kwh: an.f1_kwh ?? "",
    f2_kwh: an.f2_kwh ?? "",
    f3_kwh: an.f3_kwh ?? "",
    specific_yield: an.yield_factor ?? pvgisYield ?? "",
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(null);
  const reviewed = !!lead.technical_review?.reviewed;

  useEffect(() => {
    const a = lead.analysis || {};
    setForm({
      f1_kwh: a.f1_kwh ?? "", f2_kwh: a.f2_kwh ?? "", f3_kwh: a.f3_kwh ?? "",
      specific_yield: a.yield_factor ?? a.pvgis?.specific_yield ?? "",
    });
  }, [lead]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (confirm) => {
    setSaving(confirm ? "confirm" : "save");
    try {
      const payload = {
        f1_kwh: num(form.f1_kwh), f2_kwh: num(form.f2_kwh), f3_kwh: num(form.f3_kwh),
        specific_yield: num(form.specific_yield), confirm,
      };
      const { data } = await api.post(`/leads/${lead.id}/technical-review`, payload);
      onUpdate?.(data);
      toast.success(confirm ? "Revisione tecnica confermata" : "Dati revisione salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  if (!projectOwnerSet) {
    return (
      <Card className="rounded border-primary/40 bg-primary/5 p-4 flex items-start gap-3" data-testid="review-need-owner">
        <ClipboardCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">Imposta prima il <b>Referente di progetto</b> nel pannello a sinistra: sarà il tecnico a revisionare e confermare i dati.</div>
      </Card>
    );
  }

  const F = ({ k, label, unit, step = "1" }) => (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}{unit ? ` (${unit})` : ""}</Label>
      <Input type="number" step={step} value={form[k]} disabled={!canEdit} onChange={(e) => set(k, e.target.value)} data-testid={`review-${k}`} />
    </div>
  );

  return (
    <Card className="rounded border-border p-5" data-testid="technical-review">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading font-semibold flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Revisione tecnica</h2>
        {reviewed ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600" data-testid="review-status"><CheckCircle2 className="h-4 w-4" /> Revisionato{lead.technical_review?.by_name ? ` · ${lead.technical_review.by_name}` : ""}</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600" data-testid="review-status"><AlertTriangle className="h-4 w-4" /> Da revisionare</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-5">Il Technical Sales verifica i consumi di bolletta (F1/F2/F3) e la produttività dell'impianto. Solo i dati <b>confermati</b> vengono usati nel preventivo e negli outlook. Ogni modifica ad analisi/tetto annulla la revisione.</p>

      {!canEdit && (
        <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 mb-4 text-sm flex items-center gap-2" data-testid="review-locked">
          <Lock className="h-4 w-4 text-amber-600" /> Solo il referente di progetto ({lead.project_owner_name}) o un amministratore possono revisionare.
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <F k="f1_kwh" label="Consumo F1" unit="kWh/anno" />
        <F k="f2_kwh" label="Consumo F2" unit="kWh/anno" />
        <F k="f3_kwh" label="Consumo F3" unit="kWh/anno" />
      </div>

      <div className="mt-4 max-w-xs">
        <F k="specific_yield" label="Produttività" unit="kWh/kWp" step="1" />
        <p className="text-[11px] text-muted-foreground mt-1" data-testid="review-pvgis-hint">
          {pvgisYield ? <>Valore rilevato da <b>PVGIS</b>: {Math.round(pvgisYield)} kWh/kWp. Puoi modificarlo se necessario.</> : "Inserisci la produttività specifica dell'impianto (kWh per kWp installato)."}
        </p>
      </div>

      {canEdit && (
        <div className="flex items-center gap-3 mt-6 pt-5 border-t border-border">
          <Button variant="outline" onClick={() => submit(false)} disabled={!!saving} data-testid="review-save-btn">
            {saving === "save" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Salva bozza
          </Button>
          <Button onClick={() => submit(true)} disabled={!!saving} className="font-semibold" data-testid="review-confirm-btn">
            {saving === "confirm" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Conferma revisione
          </Button>
        </div>
      )}
    </Card>
  );
}
